import React, { useMemo, useRef, useState } from 'react'
import { Box, Flex, Text } from '@chakra-ui/core'
import { Button, EmptyState, SearchInput } from 'components/ui'
import LocaleText from 'components/LocaleText'
import { NotificationRow } from 'components/Notifications/NotificationRow'
import { NotificationRowSkeleton } from 'components/Notifications/NotificationRowSkeleton'
import { notifTokens } from 'services/notifications/designTokens'
import { groupNotificationsByDate } from 'services/notifications/groupByDate'
import { INotification } from 'services/notifications/types'
import useLocaleText from 'components/useLocaleText'

interface IActiveList {
  items: INotification[]
  isLoading: boolean
  hasLoadedOnce: boolean
  nextCursor: string | null
}

interface IProps {
  active: IActiveList
  selectedKey: string | null
  onSelect: (notification: INotification) => void
  onLoadMore: () => void
  emptyLabel: string
  isRtl?: boolean
  unreadCount: number
  onMarkAllRead: () => void
}

const LOAD_MORE_THRESHOLD_PX = 200

// Column 2 of the desktop modal (spec §2) — 392px, white. Search is a client-side substring
// filter over whatever's already loaded (title + body) — there's no server-side search endpoint
// in notifications-ms yet.
export const NotificationListColumn: React.FC<IProps> = ({
  active,
  selectedKey,
  onSelect,
  onLoadMore,
  emptyLabel,
  isRtl = false,
  unreadCount,
  onMarkAllRead,
}) => {
  const [search, setSearch] = useState('')
  const listRef = useRef<HTMLDivElement>(null)
  const t_search = useLocaleText('search')
  const t_mark_all_read = useLocaleText('mark_all_seen')
  const searched = useMemo(() => {
    if (!search.trim()) return active.items
    const q = search.trim().toLowerCase()
    return active.items.filter((n) => n.title.toLowerCase().includes(q) || n.body?.toLowerCase().includes(q))
  }, [active.items, search])

  const groups = useMemo(() => groupNotificationsByDate(searched), [searched])

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < LOAD_MORE_THRESHOLD_PX
    if (nearBottom && active.nextCursor && !active.isLoading) onLoadMore()
  }

  const onListKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
    e.preventDefault()
    const rows = Array.from(listRef.current?.querySelectorAll<HTMLButtonElement>('[data-notif-row]') ?? [])
    if (!rows.length) return
    const currentIndex = rows.indexOf(document.activeElement as HTMLButtonElement)
    const nextIndex =
      e.key === 'ArrowDown' ? Math.min(currentIndex + 1, rows.length - 1) : Math.max(currentIndex - 1, 0)
    rows[Math.max(nextIndex, 0)]?.focus()
  }

  return (
    <Flex
      w="392px"
      flexShrink={0}
      flexDirection="column"
      bg="white"
      borderRight={`1px solid ${notifTokens.hairline}`}
      h="100%"
    >
      <Flex
        alignItems="center"
        justifyContent="space-between"
        p="16px"
        borderBottom={`1px solid ${notifTokens.hairline}`}
        style={{ columnGap: '10px' }}
      >
        <Box flex="1" minW="0">
          <SearchInput
            value={search}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
            onClear={() => setSearch('')}
            placeholder={t_search}
            direction={isRtl ? 'rtl' : 'ltr'}
            variant="outline"
            elementHeight="31px"
            borderWidth="1px"
            borderColor={notifTokens.hairline}
            borderRadius="8px"
            bg={notifTokens.railBg}
            fontSize="12.5px"
            color={notifTokens.ink}
            _placeholder={{ color: notifTokens.faint }}
            _hover={{ bg: notifTokens.railBg }}
            _focus={{ borderColor: notifTokens.hairline, boxShadow: 'none', outline: 'none', bg: notifTokens.railBg }}
          />
        </Box>
        <Button
          variant="ghost"
          onClick={onMarkAllRead}
          isDisabled={unreadCount === 0}
          h="31px"
          minW="auto"
          px="10px"
          flexShrink={0}
          fontSize="12px"
          fontWeight={600}
          color={unreadCount === 0 ? notifTokens.faint : notifTokens.ink}
          _hover={{ bg: unreadCount === 0 ? 'transparent' : notifTokens.rowHover }}
          _focus={{ boxShadow: 'none', outline: 'none' }}
        >
          {t_mark_all_read}
        </Button>
      </Flex>

      <Box
        ref={listRef}
        flex="1"
        minH="0"
        overflowY="auto"
        onScroll={handleScroll}
        onKeyDown={onListKeyDown}
        p="4px 8px 14px"
      >
        {active.isLoading && !active.hasLoadedOnce && (
          <Box>
            {Array.from({ length: 10 }).map((_, index) => (
              <NotificationRowSkeleton key={index} />
            ))}
          </Box>
        )}

        {active.hasLoadedOnce && searched.length === 0 && <EmptyState defaultText={emptyLabel} />}

        {groups.map((group) => (
          <Box key={group.label} mb="2px">
            <Text
              fontSize="10.5px"
              fontWeight={600}
              color={notifTokens.faint}
              p="11px 8px 5px"
              letterSpacing="0.08em"
              textTransform="uppercase"
            >
              <LocaleText text={group.label} />
            </Text>
            {group.items.map((notification) => (
              <NotificationRow
                key={notification.chrono_key}
                notification={notification}
                isSelected={notification.chrono_key === selectedKey}
                onClick={() => onSelect(notification)}
              />
            ))}
          </Box>
        ))}

        {active.isLoading && active.hasLoadedOnce && <NotificationRowSkeleton />}
      </Box>
    </Flex>
  )
}
